'use client';

import { closestCenter, DndContext } from '@dnd-kit/core';
import { SortableContext } from '@dnd-kit/sortable';
import { cn } from 'cn';

import { ProjectResponseAudio } from '@/shared-survey';
import { getAlphabetLabel } from '@/shared-utilities';

import { ProjectResponseAssetItem } from './project-response-asset-item';
import ProjectResponseMessageRendererAudio from './project-response-message-renderer-audio';
import { ProjectResponseQuestionText } from './project-response-question-text';
import ProjectResponseRankingItem from './project-response-ranking-item';
import { useProjectResponseRanking } from './project-response-ranking.hooks';
import { ProjectResponseComponentProps } from './project-response-renderer.type';

interface ProjectResponseRankingProps {
  values: {
    label: string;
    value: string;
  }[];
  value: string;
  onChange?: (values: string) => void;
  readonly?: boolean;
  assets?: ProjectResponseComponentProps['assets'];
  text?: string;
  audio?: ProjectResponseAudio;
  isAiVoiceEnabled?: boolean;
  app?: 'manager' | 'survey';
}

export default function ProjectResponseRanking({
  app,
  value,
  values,
  onChange,
  readonly,
  assets,
  text,
  audio,
  isAiVoiceEnabled,
}: ProjectResponseRankingProps) {
  const { valueAsArray, valueIndexes, sensors, handleDragEnd } =
    useProjectResponseRanking({ value, values, onChange, readonly });
  if (readonly) {
    return (
      <div
        data-test='project-response-ranking'
        className={cn(
          'flex flex-col items-start justify-center gap-4 rounded-2xl border bg-(--brand-assistant-message-bg) px-6 py-8 shadow-(--brand-separation-shadow)',
          app
            ? 'border-(--brand-separation-border)'
            : 'border-b-2 border-gray-200'
        )}
      >
        {isAiVoiceEnabled && (
          <ProjectResponseMessageRendererAudio audio={audio} />
        )}

        {text && (
          <ProjectResponseQuestionText
            text={text}
            className={cn(app && 'text-(--brand-text-on-assistant-message)')}
          />
        )}

        {assets && assets?.length > 0 && assets[0]?.url && (
          <ProjectResponseAssetItem asset={assets[0]} />
        )}

        <div className='flex flex-col items-start justify-center gap-2 self-stretch'>
          {valueAsArray.map((value, index) => (
            <ProjectResponseRankingItem
              key={value.value}
              app={app}
              value={value.value}
              tag={getAlphabetLabel(valueIndexes[index])}
              disabled
              id={value.value}
              label={value.label}
            />
          ))}
        </div>
      </div>
    );
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={handleDragEnd}
    >
      <SortableContext items={valueAsArray.map((e) => e.value)}>
        <div
          data-test='project-response-ranking'
          className='flex touch-none flex-col items-start justify-center gap-4 rounded-2xl border border-(--brand-separation-border) bg-(--brand-assistant-message-bg) px-6 py-8 shadow-(--brand-separation-shadow) backdrop-blur-sm'
        >
          {isAiVoiceEnabled && (
            <ProjectResponseMessageRendererAudio audio={audio ?? undefined} />
          )}
          {text && (
            <ProjectResponseQuestionText
              text={text}
              className={cn(app && 'text-(--brand-text-on-assistant-message)')}
            />
          )}

          {assets && assets?.length > 0 && assets[0]?.url && (
            <ProjectResponseAssetItem asset={assets[0]} />
          )}

          <div className='flex flex-col items-start justify-center gap-2 self-stretch'>
            {valueAsArray.map((value, index) => (
              <ProjectResponseRankingItem
                key={value.value}
                app={app}
                value={value.value}
                tag={getAlphabetLabel(valueIndexes[index])}
                id={value.value}
                label={value.label}
              />
            ))}
          </div>
        </div>
      </SortableContext>
    </DndContext>
  );
}
